import * as Sentry from "@sentry/react";
import { combineEpics, Epic, ofType } from "redux-observable";
import { EMPTY, from, Observable } from "rxjs";
import {
  catchError,
  filter,
  map,
  mapTo,
  pluck,
  switchMap,
  take,
} from "rxjs/operators";
import { AuthActions } from "../auth/actions";
import {
  FetchableError,
  FetchableSuccess,
  isFetchableSuccess,
} from "../common/fetchable/helpers";
import { WalletsActions } from "./actions";
import { WalletsDTO } from "./models/dto";
import { WalletsService } from "./service";

const getWalletsEpic: Epic<WalletsActions.Actions> = (action$) =>
  action$.pipe(
    ofType(WalletsActions.ActionTypes.getWallets),
    switchMap<
      WalletsActions.Actions,
      Observable<
        FetchableError | FetchableSuccess<WalletsDTO.Responses.GetAll.T>
      >
    >(() =>
      WalletsService.getWallets({
        pagination: { size: 20, page: 0 },
      })
    ),
    map((x) => WalletsActions.setWallets(x))
  );

const setPaginationEpic: Epic<WalletsActions.Actions> = (action$) =>
  action$.pipe(
    ofType(WalletsActions.ActionTypes.setPagination),
    switchMap<
      WalletsActions.SetPagination,
      Observable<
        FetchableError | FetchableSuccess<WalletsDTO.Responses.GetAll.T>
      >
    >((action) => {
      return WalletsService.getWallets(action.payload);
    }),
    map((x) => WalletsActions.setWallets(x))
  );

const getWalletsSummaryEpic: Epic<WalletsActions.Actions> = (action$) =>
  action$.pipe(
    ofType(WalletsActions.ActionTypes.getWalletsSummary),
    switchMap<
      WalletsActions.Actions,
      Observable<
        | FetchableError
        | FetchableSuccess<WalletsDTO.Responses.GetWalletsSummary.T>
      >
    >(() => WalletsService.getWalletsSummary()),
    map((x) => WalletsActions.setWalletsSummary(x))
  );

const getTotalBalanceEpic: Epic<WalletsActions.Actions> = (action$) =>
  action$.pipe(
    ofType(WalletsActions.ActionTypes.getTotalBalance),
    switchMap<
      WalletsActions.Actions,
      Observable<
        | FetchableError
        | FetchableSuccess<WalletsDTO.Responses.GetTotalBalance.T>
      >
    >(() => WalletsService.getTotalBalance()),
    map((x) => WalletsActions.setTotalBalance(x))
  );

const refreshLoyaltyEpic: Epic<
  AuthActions.Actions | WalletsActions.Actions,
  WalletsActions.Actions
> = (action$) =>
  action$.pipe(
    ofType(AuthActions.ActionTypes.authRequest),
    switchMap<
      AuthActions.Actions | WalletsActions.Actions,
      Observable<boolean>
    >(() =>
      action$.pipe(
        ofType<
          AuthActions.Actions | WalletsActions.Actions,
          AuthActions.AuthSuccess
        >(AuthActions.ActionTypes.authDone),
        pluck("payload"),
        filter(isFetchableSuccess),
        filter((x) => !!x.data.accessToken.authenticated),
        mapTo(true),
        take(1)
      )
    ),
    switchMap<boolean, Promise<WalletsDTO.Responses.RefreshWallet.T>>(() =>
      WalletsService.refreshLoyalty()
    ),
    catchError((e) => {
      Sentry.captureException(e);
      return EMPTY;
    }),
    map((x) => WalletsActions.setLoyalty(x))
  );
const dependencies: Epic<
  AuthActions.Actions | WalletsActions.Actions,
  WalletsActions.Actions
> = (action$) =>
  action$.pipe(
    ofType(AuthActions.ActionTypes.authDone),
    switchMap(() => {
      return from([
        WalletsActions.resetTotalBalance(),
        WalletsActions.resetWallets(),
        WalletsActions.resetWalletsSummary(),
      ]);
    })
  );

export const walletsEpic = combineEpics(
  getWalletsEpic,
  setPaginationEpic,
  getWalletsSummaryEpic,
  refreshLoyaltyEpic,
  getTotalBalanceEpic,
  dependencies
);
